// ANA-R3-NAV-01 — shared "previous / next data point" primitive. The Viewer
// (via its `onArrowNavigate` prop) and any other surface that wants arrow-key
// data-point stepping consume this so they all walk the SAME ordered, filtered
// set the Filmstrip walks: `useImagesQuery` (capture order) intersected with
// `useFilteredDataPoints().visibleImageIds` (the live 7-axis filter).
//
// The Filmstrip keeps its own focus-managing keydown handler (roving tabindex),
// but lands on the identical end state because both paths converge on
// `useImageActivationStore.setActive` — the single source of the active image.
import { useCallback, useMemo } from "react";
import type { ImageRef } from "@aeriosiq/types";
import { useImagesQuery } from "./queries/useImagesQuery.js";
import { useImageActivationStore } from "./state/imageActivationStore.js";
import { useFilteredDataPoints } from "./useFilteredDataPoints.js";

export interface DataPointNavigation {
  /** Activate the data point before the active one. No-op at the start. */
  goToPrevious: () => void;
  /** Activate the data point after the active one. No-op at the end. */
  goToNext: () => void;
}

export function useDataPointNavigation(
  dataAcquisitionId: string,
): DataPointNavigation {
  const query = useImagesQuery(dataAcquisitionId);
  const filtered = useFilteredDataPoints(dataAcquisitionId);
  const activeImageId = useImageActivationStore((s) => s.activeImageId);
  const setActive = useImageActivationStore((s) => s.setActive);

  // Same reduction the Filmstrip applies (FilmstripWidget.tsx) — keep them
  // identical so the Viewer and the strip never disagree on the order or the
  // membership of the set. While the filtered query is in flight we fall back
  // to all images (avoids stepping into a transiently-empty set).
  const items = useMemo<ImageRef[]>(() => {
    const allImages = query.data?.items ?? [];
    if (filtered.isPending) return allImages;
    return allImages.filter((img) => filtered.visibleImageIds.has(img.imageId));
  }, [query.data, filtered.visibleImageIds, filtered.isPending]);

  const goToOffset = useCallback(
    (offset: number): void => {
      if (!activeImageId) return;
      const idx = items.findIndex((it) => it.imageId === activeImageId);
      if (idx === -1) return;
      const target = items[idx + offset];
      if (!target) return; // clamp at both ends — no wrap-around (OQ2)
      setActive(target.imageId);
    },
    [items, activeImageId, setActive],
  );

  const goToPrevious = useCallback(() => {
    goToOffset(-1);
  }, [goToOffset]);
  const goToNext = useCallback(() => {
    goToOffset(1);
  }, [goToOffset]);

  return { goToPrevious, goToNext };
}
